import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { MealData } from '@/types/meals';
import { UserProfile } from '@/types/nutrition';
import { Database } from '@/types/database';
import { NutritionGoals } from '@/types/nutrition';

interface NutrAIDB extends DBSchema {
  meals: {
    key: string;
    value: {
      id: string;
      data: MealData;
      syncStatus: 'pending' | 'synced';
      lastModified: number;
    };
    indexes: { 'by-status': string };
  };
  profile: {
    key: 'profile';
    value: {
      data: UserProfile;
      lastSync: number;
    };
  };
  nutritionGoals: {
    key: 'goals';
    value: {
      data: NutritionGoals;
      lastSync: number;
    };
  };
}

class OfflineStorageService {
  private db: IDBPDatabase<NutrAIDB> | null = null;
  private dbName = 'nutrai-offline-db';
  private version = 1;

  private async getDB() {
    if (!this.db) {
      this.db = await openDB<NutrAIDB>(this.dbName, this.version, {
        upgrade(db) {
          // Create meals store with index
          if (!db.objectStoreNames.contains('meals')) {
            const mealsStore = db.createObjectStore('meals', { keyPath: 'id' });
            mealsStore.createIndex('by-status', 'syncStatus');
          }

          // Create profile store
          if (!db.objectStoreNames.contains('profile')) {
            db.createObjectStore('profile');
          }

          // Create nutrition goals store
          if (!db.objectStoreNames.contains('nutritionGoals')) {
            db.createObjectStore('nutritionGoals');
          }
        },
      });
    }
    return this.db;
  }

  // Meals CRUD operations
  async saveMeal(meal: MealData) {
    const db = await this.getDB();
    const timestamp = Date.now();
    
    await db.put('meals', {
      id: meal.id,
      data: meal,
      syncStatus: 'pending',
      lastModified: timestamp,
    });
  }

  async getMeal(id: string) {
    const db = await this.getDB();
    return await db.get('meals', id);
  }

  async getAllMeals() {
    const db = await this.getDB();
    return await db.getAll('meals');
  }

  async getPendingMeals() {
    const db = await this.getDB();
    return await db.getAllFromIndex('meals', 'by-status', 'pending');
  }

  async deleteMeal(id: string) {
    const db = await this.getDB();
    await db.delete('meals', id);
  }

  // Profile operations
  async saveProfile(profile: UserProfile) {
    const db = await this.getDB();
    await db.put('profile', {
      data: profile,
      lastSync: Date.now(),
    }, 'profile');
  }

  async getProfile() {
    const db = await this.getDB();
    return await db.get('profile', 'profile');
  }

  // Nutrition goals operations
  async saveNutritionGoals(goals: NutritionGoals) {
    const db = await this.getDB();
    await db.put('nutritionGoals', {
      data: goals,
      lastSync: Date.now(),
    }, 'goals');
  }

  async getNutritionGoals() {
    const db = await this.getDB();
    return await db.get('nutritionGoals', 'goals');
  }

  // Sync status management
  async markAsSynced(id: string) {
    const db = await this.getDB();
    const meal = await this.getMeal(id);
    if (meal) {
      meal.syncStatus = 'synced';
      await db.put('meals', meal);
    }
  }

  async clearSyncedData() {
    const db = await this.getDB();
    const syncedMeals = await db.getAllFromIndex('meals', 'by-status', 'synced');
    
    // Remove meals that have been synced for more than 30 days
    const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
    for (const meal of syncedMeals) {
      if (meal.lastModified < thirtyDaysAgo) {
        await this.deleteMeal(meal.id);
      }
    }
  }
}

// Export singleton instance
export const offlineStorage = new OfflineStorageService();